// src/stores/useNotesStore.ts
// Simplified to only manage global timing (BPM, noteLength) and playback state
// Pattern-specific state moved to per-device patterns in useMusicStore

import { create } from 'zustand';
import * as Tone from 'tone';
import musicTiming from '~/data/musicTiming';
import type { NoteLength } from '~/types';
import { initializeOptimizedAudioContext } from '~/lib/audio/audioContext';

const STORAGE_KEYS = {
  BPM: 'yomidi-global-bpm',
  SWING: 'yomidi-global-swing',
} as const;

// Load saved BPM from localStorage
function loadBpm(): number {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.BPM);
    if (saved) {
      const value = parseFloat(saved);
      // Validate range (40-240 BPM)
      if (!isNaN(value) && value >= 40 && value <= 240) {
        return value;
      }
    }
  } catch (err) {
    console.warn('[STORAGE] Failed to load BPM:', err);
  }
  return 108; // Default BPM
}

// Load saved swing from localStorage
function loadSwing(): number {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.SWING);
    if (saved) {
      const value = parseFloat(saved);
      // Validate range (-50 to +50)
      if (!isNaN(value) && value >= -50 && value <= 50) {
        return value;
      }
    }
  } catch (err) {
    console.warn('[STORAGE] Failed to load swing:', err);
  }
  return 0; // Default swing (no swing)
}

// Save BPM to localStorage
function saveBpm(value: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.BPM, value.toString());
  } catch (err) {
    console.warn('[STORAGE] Failed to save BPM:', err);
  }
}

// Save swing to localStorage
function saveSwing(value: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SWING, value.toString());
  } catch (err) {
    console.warn('[STORAGE] Failed to save swing:', err);
  }
}

interface NotesState {
  // --- Global Timing (shared across all devices) ---
  bpm: number;
  swing: number; // -50 to +50, where 0 is no swing
  noteLength: NoteLength;
  scrambleNoteLength: boolean;

  // --- Playback state ---
  start: boolean;

  // --- Actions ---
  setBpm: (v: number) => void;
  setSwing: (v: number) => void;
  setNoteLength: (v: NoteLength) => void;
  toggleScrambleNoteLength: () => void;
  toggleStart: () => Promise<void>;
}

export const useNotesStore = create<NotesState>((set, get) => {
  return {
    // --- Initial global timing (loaded from localStorage) ---
    bpm: loadBpm(),
    swing: loadSwing(),
    noteLength: musicTiming.noteLengths[2], // Default to 8th notes (index 2)
    scrambleNoteLength: false,

    // --- Initial playback state ---
    start: false,

    // --- Actions ---
    setBpm: (v) => {
      saveBpm(v);
      set({ bpm: v });
    },
    setSwing: (v) => {
      const clamped = Math.max(-50, Math.min(50, v));
      saveSwing(clamped);
      set({ swing: clamped });
    },

    setNoteLength: (v) => set({ noteLength: v }),
    toggleScrambleNoteLength: () => set((state) => ({ scrambleNoteLength: !state.scrambleNoteLength })),

    toggleStart: async () => {
      const state = get();
      console.log("▶️ [PLAY BUTTON] toggleStart called, current state.start:", state.start);

      if (!state.start) {
        try {
          console.log("🎵 [PLAY BUTTON] Starting playback, initializing Tone.js audio context...");
          console.log("🎵 [TONE.JS] Audio context state BEFORE initialization:", Tone.getContext().state);
          console.log("🎵 [TONE.JS] Audio context currentTime:", Tone.getContext().currentTime);

          // Initialize audio context with platform-optimized settings
          await initializeOptimizedAudioContext();

          console.log("🔊 [TONE.JS] Audio context state after initialization:", Tone.getContext().state);
          console.log("🔊 [TONE.JS] Audio context destination:", Tone.getDestination());
          console.log("🔊 [TONE.JS] Audio context sampleRate:", Tone.getContext().sampleRate);
          console.log("🔊 [TONE.JS] Audio context latencyHint:", Tone.getContext().latencyHint);
          console.log("🔊 [TONE.JS] Audio context lookAhead:", Tone.getContext().lookAhead);
        } catch (error) {
          console.error("❌ [PLAY BUTTON] Failed to start audio context:", error);
        }
      } else {
        console.log("⏸️ [PLAY BUTTON] Stopping playback");
      }

      set({ start: !state.start });
      console.log("▶️ [PLAY BUTTON] New state.start:", !state.start);
    },
  };
});
